/**
 * AnalyticsPage
 * 
 * Form analytics dashboard displaying:
 * - Overview metrics
 * - Completion funnel
 * - A/B test variant comparison
 * 
 * PHI-SAFE: Analytics data contains only aggregate metrics,
 * no personally identifiable information.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import type { AnalyticsOverview, FunnelData, ABTestResults, Form } from '@formflow/shared';
import { analyticsApi, formsApi } from '../../lib/api';
import { DashboardLayout } from '../../components/dashboard/DashboardLayout';
import {
  DateRangePicker,
  DateRange,
  MetricsCards,
  FunnelChart,
  VariantsTable,
} from '../../components/analytics';
import './AnalyticsPage.css';

// Default date range: Last 30 days
function getDefaultDateRange(): DateRange {
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);
  
  return {
    label: 'Last 30 days',
    startDate: thirtyDaysAgo.toISOString().split('T')[0],
    endDate: today.toISOString().split('T')[0],
  };
}

export function AnalyticsPage() {
  const { formId } = useParams<{ formId: string }>();
  
  // State
  const [form, setForm] = useState<Form | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange());
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [funnel, setFunnel] = useState<FunnelData | null>(null);
  const [variants, setVariants] = useState<ABTestResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch form details
  useEffect(() => {
    async function fetchForm() {
      if (!formId) return;
      try {
        const formData = await formsApi.get(formId);
        setForm(formData);
      } catch (err) {
        console.error('Failed to fetch form:', err);
      }
    }
    fetchForm();
  }, [formId]);

  // Fetch analytics data
  const fetchAnalytics = useCallback(async () => {
    if (!formId) return;
    
    setLoading(true);
    setError(null);
    
    const params = {
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    };
    
    try {
      // Fetch all analytics in parallel
      const [overviewData, funnelData, variantsData] = await Promise.all([
        analyticsApi.getOverview(formId, params).catch(() => null),
        analyticsApi.getFunnel(formId, params).catch(() => null),
        analyticsApi.getVariants(formId, params).catch(() => null),
      ]);
      
      setOverview(overviewData);
      setFunnel(funnelData);
      setVariants(variantsData);
    } catch (err) {
      setError('Failed to load analytics. Analytics service may be temporarily unavailable.');
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [formId, dateRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleDateRangeChange = (newRange: DateRange) => {
    setDateRange(newRange);
  };

  const handleRefresh = () => {
    fetchAnalytics();
  };

  if (!formId) {
    return (
      <DashboardLayout>
        <div className="analytics-error">
          <p>Form ID is required</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="analytics-page">
        {/* Header */}
        <header className="analytics-header">
          <div className="analytics-header-left">
            <nav className="analytics-breadcrumb">
              <Link to="/dashboard/forms">Forms</Link>
              <span className="breadcrumb-separator">/</span>
              {form && (
                <>
                  <Link to={`/dashboard/forms/${formId}/edit`}>{form.name}</Link>
                  <span className="breadcrumb-separator">/</span>
                </>
              )}
              <span className="breadcrumb-current">Analytics</span>
            </nav>
            <h1 className="analytics-title">
              📊 Form Analytics
            </h1>
            {form && (
              <p className="analytics-form-name">{form.name}</p>
            )}
          </div>
          
          <div className="analytics-header-right">
            <DateRangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
            />
            <button 
              className="btn btn-secondary btn-sm"
              onClick={handleRefresh}
              disabled={loading}
              title="Refresh data"
            >
              🔄 Refresh
            </button>
          </div>
        </header>

        {/* Form Navigation Tabs */}
        <nav className="analytics-tabs">
          <Link 
            to={`/dashboard/forms/${formId}/edit`}
            className="analytics-tab"
          >
            Builder
          </Link>
          <Link 
            to={`/dashboard/forms/${formId}/submissions`}
            className="analytics-tab"
          >
            Submissions
          </Link>
          <span className="analytics-tab active">
            Analytics
          </span>
        </nav>

        {/* Error State */}
        {error && (
          <div className="analytics-error-banner">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
            <button 
              className="btn btn-ghost btn-sm"
              onClick={handleRefresh}
            >
              Try Again
            </button>
          </div>
        )}

        {/* Content */}
        <div className="analytics-content">
          {/* Overview Metrics */}
          <section className="analytics-section">
            <MetricsCards data={overview} loading={loading} />
          </section>

          {/* Funnel Chart */}
          <section className="analytics-section">
            <FunnelChart data={funnel} loading={loading} />
          </section>

          {/* A/B Test Results */}
          <section className="analytics-section">
            <VariantsTable data={variants} loading={loading} />
          </section>

          {/* PHI-Safe Notice */}
          <section className="analytics-notice">
            <div className="notice-content">
              <span className="notice-icon">🔒</span>
              <div>
                <strong>PHI-Safe Analytics</strong>
                <p>
                  This dashboard shows aggregate metrics only. No personally identifiable 
                  information or form submission content is displayed here. To view actual 
                  submission data, go to the <Link to={`/dashboard/forms/${formId}/submissions`}>Submissions</Link> page.
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default AnalyticsPage;
